import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import toast from 'components/Basic/Toast';
import { useLazyGetMparticleAudiencesQuery } from 'core/mparticle/MparticleService';
import { MparticleAudience } from 'core/mparticle/types';

type UseMparticleAudiencesParams = {
  enabled: boolean;
};

const useMparticleAudiences = ({ enabled }: UseMparticleAudiencesParams) => {
  const intl = useIntl();
  const [getMparticleAudiences] = useLazyGetMparticleAudiencesQuery();

  const [audiences, setAudiences] = useState<MparticleAudience[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setAudiences([]);
      // Reset here too: a request in flight when `enabled` drops has its finally guarded by
      // `ignore`, so this branch is the only place left to clear the loading flag.
      setIsLoading(false);
      return undefined;
    }

    // Guards against a slow response resolving after `enabled` has toggled again.
    let ignore = false;

    const loadAudiences = async () => {
      try {
        setIsLoading(true);

        const response = await getMparticleAudiences().unwrap();

        if (ignore) return;

        setAudiences(response?.audiences || []);
      } catch {
        if (ignore) return;

        setAudiences([]);

        // Fixed toastId dedupes if the effect runs more than once for one open modal.
        toast.error(
          { title: intl.formatMessage({ id: 'Loading mParticle audiences is failure!' }) },
          { toastId: 'mparticle-audiences-error' },
        );
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };

    loadAudiences();

    return () => {
      ignore = true;
    };
  }, [enabled]);

  const sortedAudiences = [...audiences].sort((a, b) => Number(b.usable) - Number(a.usable));

  const findAudience = (id?: number) => audiences.find(audience => audience.id === id);

  return { audiences, sortedAudiences, findAudience, isLoading };
};

export default useMparticleAudiences;
